// subscriptionService.js
import api from "./api";

/**
 * Busca todas as assinaturas (sem paginação).
 * @returns {Promise<axios.AxiosResponse<any>>}
 */
export function fetchSubscriptions() {
  return api.get("/subscriptions");
}

/**
 * Busca uma assinatura pelo seu ID.
 * @param {number} id - O ID da assinatura.
 * @returns {Promise<axios.AxiosResponse<any>>}
 */
export function getSubscriptionById(id) {
  return api.get(`/subscriptions/${id}`);
}

/**
 * Cria uma nova assinatura.
 * @param {object} data - Os dados da assinatura.
 * @returns {Promise<axios.AxiosResponse<any>>}
 */
export function createSubscription(data) {
  return api.post("/subscriptions", data);
}

/**
 * NOVO: Realiza o upgrade de uma assinatura existente para um novo plano.
 * @param {object} data - Os dados da nova assinatura (planId, etc.).
 * @returns {Promise<axios.AxiosResponse<any>>}
 */
export function upgradeSubscription(data) {
  return api.post("/subscriptions/upgrade", data);
}

/**
 * Atualiza uma assinatura existente.
 * @param {object} data - Os dados da assinatura a ser atualizada, incluindo subscriptionId.
 * @returns {Promise<axios.AxiosResponse<any>>}
 */
export function updateSubscription(data) {
  return api.put(`/subscriptions/${data.subscriptionId}`, data);
}

/**
 * Exclui uma assinatura pelo seu ID.
 * @param {number} id - O ID da assinatura.
 * @returns {Promise<axios.AxiosResponse<any>>}
 */
export function deleteSubscription(id) {
  return api.delete(`/subscriptions/${id}`);
}

/**
 * Exclui múltiplas assinaturas em lote.
 * @param {number[]} ids - Uma lista de IDs de assinaturas.
 * @returns {Promise<axios.AxiosResponse<any>>}
 */
export function deleteSubscriptionsBatch(ids) {
  return api.delete("/subscriptions", {
    data: ids,
  });
}

/**
 * Busca assinaturas de forma paginada com filtros opcionais aplicados no backend.
 * ✅ MUDANÇA: aceita objeto de filtros completo — todos os campos vão como query params,
 *            garantindo que a paginação reflita o conjunto filtrado (não só a página atual).
 *
 * @param {number} pageNumber
 * @param {number} pageSize
 * @param {object} filters - { subscriptionId, userId, planName, paymentMethod, status, paymentStatus }
 * @returns {Promise<axios.AxiosResponse<any>>}
 */
export function fetchSubscriptionsPaged(pageNumber, pageSize, filters = {}) {
  // ✅ MUDANÇA: monta params ignorando valores vazios/nulos para não poluir a query string
  const params = { pageNumber, pageSize };

  if (filters.subscriptionId?.trim())
    params.subscriptionId = filters.subscriptionId.trim();
  if (filters.userId?.trim()) params.userId = filters.userId.trim();
  if (filters.userName?.trim()) params.userName = filters.userName.trim();
  // ✅ BUG FIX: era filters.planId → params.planId (nome antigo, ignorado pelo backend).
  //            Corrigido para filters.planName → params.planName, alinhando com:
  //            - useSubscriptionStore.js  (filters.planName)
  //            - SubscriptionsController  ([FromQuery] string? planName)
  //            - SubscriptionsService     (planNameFilter)
  //            - SubscriptionRepository   (planNameSearch + EF LIKE s.Plan.Name)
  if (filters.planName?.trim()) params.planName = filters.planName.trim();
  if (filters.paymentMethod?.trim())
    params.paymentMethod = filters.paymentMethod.trim();
  if (filters.status?.trim()) params.status = filters.status.trim();
  if (filters.paymentStatus?.trim())
    params.paymentStatus = filters.paymentStatus.trim();

  return api.get("/subscriptions/paged", { params });
}

/**
 * Cancela uma assinatura.
 * Usa o endpoint: POST /api/payment/cancel/{subscriptionId}
 */
export function cancelSubscription({
  subscriptionId,
  cancelAtPeriodEnd,
  reason,
}) {
  // Se seu api já tem baseURL "/api", esta linha vira "/api/payment/cancel/123"
  return api.post(`/payment/cancel/${subscriptionId}`, {
    cancelAtPeriodEnd,
    reason,
  });
}

/**
 * ✅ MUDANÇA: Valida um cupom de desconto enviando também o planId.
 * @param {string} couponCode - O código do cupom.
 * @param {number} planId - O ID do plano selecionado para cálculo do desconto.
 * @returns {Promise<axios.AxiosResponse<any>>}
 */
export function validateCoupon(couponCode, planId) {
  return api.get(`/payment/validate-coupon`, {
    params: {
      code: couponCode,
      planId: planId,
    },
  });
}

// ✅ NOVO: POST /api/Subscriptions/courtesy
export const grantCourtesy = (payload) =>
  api.post("/Subscriptions/courtesy", payload);

// ✅ NOVO: GET /api/Subscriptions/user/{userId}
export function fetchSubscriptionsByUserId(userId) {
  return api.get(`/subscriptions/user/${userId}`);
}

// ✅ NOVO: POST /api/Subscriptions/expire-overdue
// Expira todas as assinaturas vencidas de uma vez.
// Retorna ExpireOverdueResultDto { totalExpired, affectedUsers[] }
export function expireOverdue() {
  return api.post("/subscriptions/expire-overdue");
}
 
/**
 * Verifica se a assinatura é elegível para estorno e downgrade.
 * GET /api/subscriptions/{id}/refund-eligibility
 *
 * @param {number} subscriptionId
 * @returns {Promise<axios.AxiosResponse<{ eligible: boolean, message: string, windowEndsAt: string|null }>>}
 */
export function checkRefundEligibility(subscriptionId) {
  return api.get(`/subscriptions/${subscriptionId}/refund-eligibility`);
}
 
/**
 * Solicita o estorno e rebaixa a assinatura para o plano Free.
 * POST /api/subscriptions/{id}/refund-and-downgrade
 *
 * @param {number} subscriptionId
 * @returns {Promise<axios.AxiosResponse<{ subscriptionId, refundSucceeded, mpRefundStatus, message }>>}
 */
export function refundAndDowngrade(subscriptionId) {
  return api.post(`/subscriptions/${subscriptionId}/refund-and-downgrade`);
}

/**
 * Reativa uma assinatura pausada.
 * Usa o endpoint: POST /api/payment/reactivate/{subscriptionId}
 *
 * Comportamento esperado no backend:
 *   - Validar que sub.PreapprovalStatus == Paused
 *   - PUT /preapproval/{id} no MP com status="authorized"
 *   - Atualizar sub.PreapprovalStatus = Authorized localmente
 *   - Retornar a sub atualizada
 *
 * @param {{ subscriptionId: number, reason?: string|null }} payload
 * @returns {Promise<axios.AxiosResponse<any>>}
 */
export function reactivateSubscription({ subscriptionId, reason }) {
  return api.post(`/payment/reactivate/${subscriptionId}`, {
    reason,
  });
}